import { todayStr } from "../lib/date";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { getReferrals, createReferral, updateReferral, getStudents, getDistricts } from "../lib/api";
import { ReferralEditor } from "../components/referrals/ReferralEditor";
import ReferralTrendsDashboard from "../components/referrals/ReferralTrendsDashboard";
import { REFERRAL_OUTCOME_REASONS } from "../lib/referralOutcomes";
import { useAuth } from "../App";

const STATUS_BADGE = {
  in_process: "bg-blue-100 text-blue-700",
  waitlist: "bg-amber-100 text-amber-700",
  declined: "bg-red-100 text-red-700",
};
const STATUS_LABEL = { in_process: "In Process", waitlist: "Waitlist", declined: "Declined" };
const todayISO = () => todayStr();
const fmt = (d) => (d ? d : "—");

export default function ReferralTrackerPage() {
  const navigate = useNavigate();
  const { isAdmin, hasRole } = useAuth();
  const canViewTrends = isAdmin() || hasRole("HQ Viewer");
  const [tab, setTab] = useState("active");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRef, setNewRef] = useState({ first_name: "", last_name: "", grade: "", referred_from: "", sending_district: "", referral_best_contact: "", referral_best_phone: "", referral_notes: "", packet_url: "", linked_student_id: "" });
  const [linkMode, setLinkMode] = useState("new"); // "new" | "existing"
  const [existingStudents, setExistingStudents] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReferrals(tab === "archived");
      setReferrals(res.data);
    } catch {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sending-school / district master list for the referral dropdown.
  useEffect(() => {
    getDistricts().then((r) => {
      const list = (r.data || []).filter((d) => d?.name).sort((a, b) => a.name.localeCompare(b.name));
      setDistricts(list);
    }).catch(() => {});
  }, []);

  // Existing student files (current + former) available for linking a referral.
  useEffect(() => {
    if (!addOpen || existingStudents.length) return;
    Promise.all([getStudents("current"), getStudents("former")])
      .then(([c, f]) => {
        const all = [...(c.data || []), ...(f.data || [])];
        all.sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));
        setExistingStudents(all);
      })
      .catch(() => {});
  }, [addOpen, existingStudents.length]);

  const resetNewRef = () => {
    setNewRef({ first_name: "", last_name: "", grade: "", referred_from: "", sending_district: "", referral_best_contact: "", referral_best_phone: "", referral_notes: "", packet_url: "", linked_student_id: "" });
    setLinkMode("new");
  };

  const handleAdd = async () => {
    if (!newRef.first_name.trim() || !newRef.last_name.trim()) { toast.error("First and last name are required"); return; }
    setAdding(true);
    try {
      await createReferral({ ...newRef, linked_student_id: linkMode === "existing" ? newRef.linked_student_id : "" });
      toast.success("Referral added to the tracker");
      setAddOpen(false);
      resetNewRef();
      setTab("active");
      fetchData();
    } catch {
      toast.error("Failed to add referral");
    } finally {
      setAdding(false);
    }
  };

  const onSaved = (updated, accepted) => {
    setEditing(null);
    if (accepted) toast.success("Removed from active tracker — now a current student.");
    fetchData();
  };

  const [statusUpdating, setStatusUpdating] = useState(null);
  const handleQuickStatus = async (r, status) => {
    setStatusUpdating(r.id);
    try {
      await updateReferral(r.id, { referral_status: status });
      const msg = { waitlist: "Moved to Waitlist", declined: "Marked as Declined", in_process: "Reactivated — back in the active pipeline" }[status];
      toast.success(msg);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating(null);
    }
  };

  // Waitlist / Decline require a date + reason (+ optional notes) captured in a
  // modal BEFORE the status change is saved.
  const [outcome, setOutcome] = useState(null); // { referral, status }
  const [outcomeForm, setOutcomeForm] = useState({ date: "", reason: "", notes: "" });
  const [savingOutcome, setSavingOutcome] = useState(false);
  const openOutcome = (r, status) => {
    setOutcomeForm({ date: todayISO(), reason: "", notes: "" });
    setOutcome({ referral: r, status });
  };
  const submitOutcome = async () => {
    if (!outcomeForm.reason) { toast.error("Please select a reason"); return; }
    const { referral: r, status } = outcome;
    setSavingOutcome(true);
    try {
      const payload = {
        referral_status: status,
        outcome_reason: outcomeForm.reason,
        outcome_notes: outcomeForm.notes || "",
      };
      if (status === "waitlist") payload.waitlist_date = outcomeForm.date;
      else payload.declined_date = outcomeForm.date;
      await updateReferral(r.id, payload);
      toast.success(status === "waitlist" ? "Moved to Waitlist" : "Marked as Declined");
      setOutcome(null);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setSavingOutcome(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Referral Tracker</h1>
          <p className="text-muted-foreground text-sm">Track prospective students from referral through enrollment.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-referral-btn">+ Add Referral</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active-referrals">Active (In Process)</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived-referrals">Archived (Waitlist / Declined)</TabsTrigger>
          {canViewTrends && <TabsTrigger value="trends" data-testid="tab-referral-trends">Trends Dashboard</TabsTrigger>}
        </TabsList>

        {canViewTrends && (
          <TabsContent value="trends" className="mt-4">
            <ReferralTrendsDashboard />
          </TabsContent>
        )}

        <TabsContent value={tab === "trends" ? "__none__" : tab} className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{tab === "active" ? "Active Candidates" : "Archived Candidates"}</CardTitle>
              <CardDescription>{loading ? "Loading…" : `${referrals.length} ${tab === "active" ? "in process" : "archived"}`}</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {referrals.length === 0 && !loading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No referrals here yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Referred From</TableHead>
                      <TableHead>Best Contact</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>First Contact</TableHead>
                      <TableHead>Phone Screen</TableHead>
                      <TableHead>Tour</TableHead>
                      <TableHead>Class Visit</TableHead>
                      <TableHead>Interview</TableHead>
                      <TableHead>Packet</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((r) => (
                      <TableRow key={r.id} data-testid={`referral-row-${r.id}`}>
                        <TableCell>
                          <button className="text-blue-700 hover:underline font-medium" onClick={() => navigate(`/students/${r.id}`)} data-testid={`referral-file-link-${r.id}`}>
                            {r.first_name} {r.last_name}
                          </button>
                          {r.linked_student_id ? (
                            <button
                              className="block text-[11px] text-teal-700 hover:underline mt-0.5"
                              onClick={() => navigate(`/students/${r.linked_student_id}`)}
                              data-testid={`referral-linked-link-${r.id}`}
                            >
                              ↳ Linked to existing file
                            </button>
                          ) : null}
                        </TableCell>
                        <TableCell>{r.grade || "—"}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[r.referral_status] || "bg-gray-100 text-gray-600"}`}>
                            {STATUS_LABEL[r.referral_status] || r.referral_status || "In Process"}
                          </span>
                          {(r.referral_status === "waitlist" || r.referral_status === "declined") && (r.outcome_reason || r.waitlist_date || r.declined_date) ? (
                            <div className="text-[11px] text-muted-foreground mt-1 leading-tight" data-testid={`referral-outcome-summary-${r.id}`}>
                              {r.outcome_reason ? <span className="font-medium">{r.outcome_reason}</span> : null}
                              {(r.referral_status === "waitlist" ? r.waitlist_date : r.declined_date)
                                ? <span>{r.outcome_reason ? " · " : ""}{r.referral_status === "waitlist" ? r.waitlist_date : r.declined_date}</span>
                                : null}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>{r.referred_from || "—"}</TableCell>
                        <TableCell>{r.referral_best_contact || "—"}</TableCell>
                        <TableCell>{r.referral_best_phone || "—"}</TableCell>
                        <TableCell>{fmt(r.first_contact_date)}</TableCell>
                        <TableCell>{fmt(r.phone_screen_date)}</TableCell>
                        <TableCell>{fmt(r.tour_date)}</TableCell>
                        <TableCell>{fmt(r.class_visit_date)}</TableCell>
                        <TableCell>{fmt(r.interview_date)}</TableCell>
                        <TableCell>
                          {r.packet_url ? (
                            <a href={r.packet_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1" data-testid={`referral-packet-link-${r.id}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                              View
                            </a>
                          ) : <span className="text-gray-300">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 justify-end">
                            {tab === "active" ? (
                              <>
                                <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" disabled={statusUpdating === r.id} onClick={() => openOutcome(r, "waitlist")} data-testid={`referral-waitlist-${r.id}`}>Waitlist</Button>
                                <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" disabled={statusUpdating === r.id} onClick={() => openOutcome(r, "declined")} data-testid={`referral-decline-${r.id}`}>Decline</Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" disabled={statusUpdating === r.id} onClick={() => handleQuickStatus(r, "in_process")} data-testid={`referral-reactivate-${r.id}`}>Reactivate</Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`referral-edit-${r.id}`}>Manage</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Referral Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Referral</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            {/* Link to an existing student file, or flag as a brand-new person */}
            <div className="space-y-2 rounded-md border p-3 bg-slate-50">
              <Label className="text-xs font-semibold">This referral is for…</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => { setLinkMode("new"); setNewRef((p) => ({ ...p, linked_student_id: "" })); }}
                  className={`flex-1 text-left px-3 py-2 rounded-md border text-sm transition ${linkMode === "new" ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-slate-200 bg-white hover:bg-slate-100"}`}
                  data-testid="referral-link-mode-new"
                >
                  A new student file
                </button>
                <button
                  type="button"
                  onClick={() => setLinkMode("existing")}
                  className={`flex-1 text-left px-3 py-2 rounded-md border text-sm transition ${linkMode === "existing" ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-slate-200 bg-white hover:bg-slate-100"}`}
                  data-testid="referral-link-mode-existing"
                >
                  Link to an existing file
                </button>
              </div>
              {linkMode === "existing" && (
                <div className="space-y-1 pt-1">
                  <Label className="text-xs">Existing Student</Label>
                  <Select
                    value={newRef.linked_student_id || ""}
                    onValueChange={(v) => {
                      const s = existingStudents.find((x) => x.id === v);
                      setNewRef((p) => ({
                        ...p,
                        linked_student_id: v,
                        first_name: s?.first_name || p.first_name,
                        last_name: s?.last_name || p.last_name,
                        grade: s?.grade || p.grade,
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="referral-linked-student-select"><SelectValue placeholder="Select a student file…" /></SelectTrigger>
                    <SelectContent>
                      {existingStudents.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.last_name}, {s.first_name}{s.grade ? ` · ${s.grade}` : ""}{s.status === "former" ? " (Former)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">Links this referral to the chosen file for reference. It does not change that student&apos;s existing data.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">First Name *</Label>
              <Input value={newRef.first_name} onChange={(e) => setNewRef((p) => ({ ...p, first_name: e.target.value }))} data-testid="new-referral-first" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Last Name *</Label>
              <Input value={newRef.last_name} onChange={(e) => setNewRef((p) => ({ ...p, last_name: e.target.value }))} data-testid="new-referral-last" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Grade</Label>
              <Input value={newRef.grade} onChange={(e) => setNewRef((p) => ({ ...p, grade: e.target.value }))} data-testid="new-referral-grade" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Referred From</Label>
              <Input value={newRef.referred_from} onChange={(e) => setNewRef((p) => ({ ...p, referred_from: e.target.value }))} placeholder="Person who referred them" data-testid="new-referral-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sending District</Label>
              <Select value={newRef.sending_district || undefined} onValueChange={(v) => setNewRef((p) => ({ ...p, sending_district: v }))}>
                <SelectTrigger data-testid="new-referral-sending-district"><SelectValue placeholder="Select a sending district…" /></SelectTrigger>
                <SelectContent>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Best Contact</Label>
              <Input value={newRef.referral_best_contact} onChange={(e) => setNewRef((p) => ({ ...p, referral_best_contact: e.target.value }))} data-testid="new-referral-contact" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Contact Phone</Label>
              <Input value={newRef.referral_best_phone} onChange={(e) => setNewRef((p) => ({ ...p, referral_best_phone: e.target.value }))} data-testid="new-referral-phone" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Referral Packet Link</Label>
              <div className="flex gap-2">
                <Input value={newRef.packet_url} onChange={(e) => setNewRef((p) => ({ ...p, packet_url: e.target.value }))} placeholder="https://drive.google.com/... (link to the referral packet)" data-testid="new-referral-packet-url" />
                {newRef.packet_url ? (
                  <Button type="button" variant="outline" onClick={() => window.open(newRef.packet_url, "_blank", "noopener")} data-testid="new-referral-packet-open">Open</Button>
                ) : null}
              </div>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={newRef.referral_notes} onChange={(e) => setNewRef((p) => ({ ...p, referral_notes: e.target.value }))} data-testid="new-referral-notes" />
            </div>
          </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); resetNewRef(); }}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding} data-testid="new-referral-save-btn">{adding ? "Adding..." : "Add Referral"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Referral Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `${editing.first_name} ${editing.last_name}` : "Referral"}</DialogTitle>
          </DialogHeader>
          {editing && <ReferralEditor referral={editing} onSaved={onSaved} />}
        </DialogContent>
      </Dialog>

      {/* Waitlist / Decline outcome capture Dialog */}
      <Dialog open={!!outcome} onOpenChange={(o) => !o && setOutcome(null)}>
        <DialogContent className="max-w-md" data-testid="referral-outcome-dialog">
          <DialogHeader>
            <DialogTitle>
              {outcome?.status === "waitlist" ? "Move to Waitlist" : "Decline Referral"}
              {outcome ? ` — ${outcome.referral.first_name} ${outcome.referral.last_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {outcome && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">{outcome.status === "waitlist" ? "Waitlist Date" : "Rejection Date"}</Label>
                <Input type="date" value={outcomeForm.date} onChange={(e) => setOutcomeForm((p) => ({ ...p, date: e.target.value }))} data-testid="outcome-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason <span className="text-red-600">*</span></Label>
                <Select value={outcomeForm.reason || undefined} onValueChange={(v) => setOutcomeForm((p) => ({ ...p, reason: v }))}>
                  <SelectTrigger data-testid="outcome-reason-select"><SelectValue placeholder="Select a reason…" /></SelectTrigger>
                  <SelectContent>
                    {REFERRAL_OUTCOME_REASONS.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`outcome-reason-${r}`}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={3} value={outcomeForm.notes} onChange={(e) => setOutcomeForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Additional context" data-testid="outcome-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutcome(null)} data-testid="outcome-cancel-btn">Cancel</Button>
            <Button onClick={submitOutcome} disabled={savingOutcome || !outcomeForm.reason} data-testid="outcome-save-btn">
              {savingOutcome ? "Saving..." : (outcome?.status === "waitlist" ? "Save Waitlist" : "Save Decline")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
